// Copyright (c) 2022 by Move Closer

import { MappingConfig } from '@movecloser/front-core'

import { logger } from '../../../support'

import {
  AbstractTypeDriver,
  AnyDescription,
  DriverConfig,
  RelatedRecord,
  RelatedType,
  ResolvesDriver
} from '../../shared/related'
import { FileRelatedType } from '../../media/related.types'

import { AuthorData, ContentData, DescriptionOfAuthor, DescriptionOfContent } from '../contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export class AuthorDriver extends AbstractTypeDriver<DescriptionOfAuthor, AuthorData> {
  protected adapterMap: MappingConfig = {}
  protected dependencies: RelatedType[] = [FileRelatedType]
  protected key: RelatedType = AuthorRelatedType

  /**
   * @inheritDoc
   */
  protected resolveNested (
    unresolved: DescriptionOfAuthor,
    record: RelatedRecord,
    config: DriverConfig,
    driverResolver?: ResolvesDriver
  ): AnyDescription {
    if (typeof driverResolver === 'undefined') {
      throw new Error('[AuthorRelatedType]: Missing driverResolver.')
    }

    let avatar
    if (unresolved.avatar) {
      try {
        const fileDriver = driverResolver(FileRelatedType)
        avatar = fileDriver.resolve(`${unresolved.avatar.value}`, record, config)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    return { ...unresolved, avatar }
  }
}

export const AuthorRelatedType: RelatedType = 'author'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export class ContentDriver extends AbstractTypeDriver<DescriptionOfContent, ContentData> {
  protected adapterMap: MappingConfig = {}
  protected dependencies: RelatedType[] = [AuthorRelatedType, FileRelatedType]
  protected key: RelatedType = ContentRelatedType

  /**
   * @inheritDoc
   */
  protected resolveNested (
    unresolved: DescriptionOfContent,
    record: RelatedRecord,
    config: DriverConfig,
    driverResolver?: ResolvesDriver
  ): AnyDescription {
    if (typeof driverResolver === 'undefined') {
      throw new Error('[ContentRelatedType]: Missing driverResolver.')
    }

    let author
    let cover

    if (typeof unresolved.author !== 'undefined') {
      try {
        const authorDriver = driverResolver(AuthorRelatedType)
        author = authorDriver.resolve(`${unresolved.author.value}`, record, config, driverResolver)
      } catch (e) {
        logger(e, 'warn')
      }
    }

    if (typeof unresolved.properties.cover === 'object' && unresolved.properties.cover !== null) {
      cover = { ...unresolved.properties.cover }

      try {
        const fileDriver = driverResolver(FileRelatedType)
        cover.image = fileDriver.resolve(`${unresolved.properties.cover.image.value}`, record, config)
      } catch (e) {
        cover = undefined
        logger(e, 'warn')
      }
    }

    return {
      ...unresolved,
      properties: {
        ...unresolved.properties,
        cover
      },
      author
    }
  }
}

// TODO: in meta.related we get `article`
export const ContentRelatedType: RelatedType = 'article'
